/**
 * Show a stored business day the way the *tenant* has decided dates are
 * written.
 *
 * A `date` column carries a day, and the API delivers it as the day it is —
 * '2026-04-01'. Printed straight onto a screen it reads as `2026-04-01`,
 * which is nobody's way of writing a date; printed through `new Date(value)`
 * it becomes an instant again, and an instant has a zone — which is the very
 * conversion that had a work order valid from the 1st of April displayed as
 * `2026-03-31T18:30:00.000Z` on the Work Orders screen.
 *
 * So the day is rebuilt from its own three numbers at local midday and then
 * written out. Local midday cannot fall into the previous or next day whatever
 * zone the reader is in, so the date shown is always the date stored.
 *
 * WHO DECIDES THE ARRANGEMENT
 *   The tenant, through the DISPLAY_DATE_FORMAT system parameter (m01_045),
 *   which a tenant administrator sets on System Configuration and which
 *   arrives on every screen with the tenant identity.
 *
 *   It used to be `new Intl.DateTimeFormat(locale)` — the reader's own
 *   language picker. That meant the same stored row read 01.04.2026 to one
 *   user and 4/1/2026 to the next, and on the Work Orders screen of an Indian
 *   tenant a validity window really did print as `9/1/2026 → 3/31/2027`
 *   because that reader's Language was "English (US)". A date is the tenant's
 *   convention, not the reader's machine's, so the tenant now decides it.
 *
 *   `locale` survives for one job only: a format that *names* its month
 *   (DD-MMM-YYYY) must name it in the reader's own language. The five purely
 *   numeric formats do not use it at all.
 *
 * Anything that is not a plain day is returned untouched rather than guessed
 * at, so a malformed value shows itself instead of silently becoming a
 * plausible wrong date.
 */

/**
 * The formats a tenant may choose, and the only ones this file renders.
 *
 * This list and the `allowed_enum` of the DISPLAY_DATE_FORMAT catalogue row
 * are the same set stated in two languages, so a test asserts they agree —
 * an administrator must never be offered a format the screens cannot write.
 */
export const BUSINESS_DAY_FORMATS = [
  'DD.MM.YYYY',
  'DD/MM/YYYY',
  'DD-MM-YYYY',
  'DD-MMM-YYYY',
  'MM/DD/YYYY',
  'YYYY-MM-DD',
] as const;

export type BusinessDayFormat = (typeof BUSINESS_DAY_FORMATS)[number];

/**
 * What a tenant that has chosen nothing sees. Identical to the
 * `default_value_text` of the catalogue row, which is the authority.
 */
export const DEFAULT_BUSINESS_DAY_FORMAT: BusinessDayFormat = 'DD.MM.YYYY';

/** Type-guard: is `value` a format these screens know how to write. */
export function isBusinessDayFormat(
  value: unknown,
): value is BusinessDayFormat {
  return (
    typeof value === 'string' &&
    (BUSINESS_DAY_FORMATS as readonly string[]).includes(value)
  );
}

export function resolveBusinessDayFormat(value: string): BusinessDayFormat {
  return isBusinessDayFormat(value) ? value : DEFAULT_BUSINESS_DAY_FORMAT;
}

/** Read the tenant's date notation without changing its calendar day or zone. */
export function parseBusinessDayInput(
  value: string,
  format: string,
  locale: string,
): string | null {
  const escape = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const monthNames = resolveBusinessDayFormat(format).includes('MMM')
    ? Array.from({ length: 12 }, (_, month) => {
        const at = new Date(0);
        at.setUTCMonth(month, 15);
        return new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(at);
      })
    : [];
  const tokens: string[] = [];
  const pattern = resolveBusinessDayFormat(format).split(/(YYYY|MMM|MM|DD)/).map((part) => {
    if (!/^(YYYY|MMM|MM|DD)$/.test(part)) return escape(part);
    tokens.push(part);
    if (part === 'YYYY') return '(\\d{4})';
    if (part === 'MMM') return `(${monthNames.map(escape).join('|')})`;
    return '(\\d{1,2})';
  }).join('');
  const match = new RegExp(`^${pattern}$`, 'iu').exec(value.trim());
  if (!match) return null;
  const parts = new Map(tokens.map((token, index) => [token, match[index + 1]]));
  const year = Number(parts.get('YYYY'));
  const month = parts.has('MMM')
    ? monthNames.findIndex((name) =>
        name.toLocaleLowerCase(locale) === parts.get('MMM')?.toLocaleLowerCase(locale)) + 1
    : Number(parts.get('MM'));
  const day = Number(parts.get('DD'));
  if (year < 1 || month < 1 || month > 12 || day < 1) return null;
  const last = new Date(0);
  last.setUTCFullYear(year, month, 0);
  if (day > last.getUTCDate()) return null;
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// MMM before MM, or 'DD-MMM-YYYY' would match MM and leave a stray 'M'.
const TOKENS = /YYYY|MMM|MM|DD/g;

/**
 * Write one already-decided calendar day in `format`.
 *
 * The three public functions below differ only in how they arrive at the day —
 * from a stored 'YYYY-MM-DD', or from an instant read on the reader's clock.
 * Once they have it they must agree exactly, so the writing itself lives here
 * and not three times over.
 *
 * `at` is handed in for the month name alone: `Intl` needs a Date to name a
 * month, and it must be the same day the numbers describe.
 */
function writeDay(
  format: string,
  year: string,
  month: string,
  day: string,
  locale: string,
  at: Date,
): string {
  const pattern = resolveBusinessDayFormat(format);
  return pattern.replace(TOKENS, (token) => {
    switch (token) {
      case 'YYYY':
        return year;
      case 'MMM':
        return new Intl.DateTimeFormat(locale, { month: 'short' }).format(at);
      case 'MM':
        return month;
      default:
        return day;
    }
  });
}

/** The reader's own calendar day for an instant, as its three numbers. */
function partsOf(at: Date): { year: string; month: string; day: string } {
  return {
    year: String(at.getFullYear()),
    month: String(at.getMonth() + 1).padStart(2, '0'),
    day: String(at.getDate()).padStart(2, '0'),
  };
}

/**
 * A business day ('YYYY-MM-DD') written in the tenant's chosen `format`.
 *
 * `locale` names the month for DD-MMM-YYYY and is unused by the rest. An
 * unrecognised `format` falls back to the shipped default rather than printing
 * its own tokens at the reader.
 */
export function formatBusinessDay(
  value: string | null | undefined,
  format: string,
  locale: string,
): string {
  if (!value) return '\u2014';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) return String(value);
  const [, year, month, day] = m;
  // Midday, so no timezone can move it off its own day.
  const at = new Date(Number(year), Number(month) - 1, Number(day), 12);
  return writeDay(format, year, month, day, locale, at);
}

/** Two days as a validity window, each written in the tenant's `format`. */
export function formatBusinessDayRange(
  from: string | null | undefined,
  to: string | null | undefined,
  format: string,
  locale: string,
): string {
  if (!from && !to) return '\u2014';
  return `${formatBusinessDay(from, format, locale)} \u2192 ${formatBusinessDay(to, format, locale)}`;
}

/**
 * An instant — created-at, submitted-at, approved-at — as the day it happened
 * on plus the time of day.
 *
 * A timestamp is not a business day: it is a moment, and it carries a zone. So
 * it is converted to the reader's own clock first and *then* the calendar day
 * of that moment is written in the tenant's format. The day a reader sees is
 * therefore the day it was for him, which is what "created at" means to the
 * person reading it.
 *
 * THE TIME OF DAY IS STILL THE READER'S
 *   DISPLAY_DATE_FORMAT says how a *date* is written and nothing about clocks.
 *   Whether 14:05 or 2:05 pm is right is a different question with a different
 *   answer, and inventing a second setting for it here — unasked — would be
 *   adding configuration nobody requested. So the time is left to `Intl` and
 *   the reader's locale, and only the date obeys the tenant.
 *
 * A value that is not a readable instant is returned as it came rather than
 * shown as a plausible wrong moment.
 */
export function formatTimestamp(
  value: string | number | Date | null | undefined,
  format: string,
  locale: string,
): string {
  if (value === null || value === undefined || value === '') return '\u2014';
  const at = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(at.getTime())) return String(value);
  const { year, month, day } = partsOf(at);
  const time = new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(
    at,
  );
  return `${writeDay(format, year, month, day, locale, at)}, ${time}`;
}

/**
 * The day an instant fell on, without the time of day.
 *
 * Nineteen screens show only the date of a created-at or a certified-at — a
 * claim register wants the day a claim was raised, not the minute. They used
 * `toLocaleDateString`, which took its arrangement from the reader.
 *
 * WHY THIS IS NOT `formatBusinessDay`
 *   A stored business day is three numbers and nothing else. An instant is a
 *   moment with a zone, and the day it belongs to depends on who is looking:
 *   `2026-10-14T20:30:00Z` is the 14th in London and already the 15th in
 *   India. Feeding such a string to `formatBusinessDay` would print the 14th
 *   to an Indian reader, because the first ten characters say so — a wrong
 *   day, confidently rendered. So the instant is read on the reader's own
 *   clock first, and only then written in the tenant's format.
 */
export function formatInstantDay(
  value: string | number | Date | null | undefined,
  format: string,
  locale: string,
): string {
  if (value === null || value === undefined || value === '') return '\u2014';
  const at = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(at.getTime())) return String(value);
  const { year, month, day } = partsOf(at);
  return writeDay(format, year, month, day, locale, at);
}
